import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Box, Collapse, Typography, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useToast } from 'use-toast-mui';
import { getAllManagerList } from '../../api/member-management';
import { pageSize, appBrandingTypes } from '../../constants/constants';
import { FormInputDate } from '../../form-components/FormInputDate';
import { FormInputText } from '../../form-components/FormInputText';
import CommonSearchSubmitButton from '../../form-components/CommonSearchSubmitButton';
import CommonFilterActionButton from '../../form-components/CommonFilterActionButton';
import CommonFilterToggle from '../../form-components/CommonFilterToggle';
import CommonPagination from '../../form-components/CommonPagination';
import CommonSelect from '../../form-components/CommonSelect';
import { CommonExcelButton } from '../../form-components/CommonExcelButton';
import AnimationPage from '../../pages/AnimationPage';
import { errorMessages } from '../../utils/methods';
import FilterButtons from '../FilterButtons';
import Loader from '../loader';
import CorpManagementTable from './CorpManagementTable';
import CorpExcelUploadModal from './corpExcelUpload/CorpExcelUploadModal';
import * as XLSX from 'xlsx';

// ============================================
// Styled Components
// ============================================
const PageContainer = styled(Box)({
  background: '#fff',
  borderRadius: '10px',
  padding: '30px 40px 60px 40px',
});

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const SearchSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const PageTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 700,
  color: '#111',
  marginRight: '16px',
});

const CreateButton = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '38px',
  padding: '0 20px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: '#0F7F85',
  textDecoration: 'none',
  '&:hover': {
    backgroundColor: '#0a6a6f',
  },
});

const HeaderButtons = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const ExcelUploadButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '38px',
  padding: '0 20px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#0F7F85',
  backgroundColor: '#F8FBFB',
  border: '1px solid #0F7F85',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#eef6f6',
  },
});

const FilterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginTop: '20px',
  gap: '12px',
});

const FilterLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#333',
  minWidth: '80px',
});

const SearchButtonRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginTop: '20px',
});

const LimitSelectRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: '20px',
  marginBottom: '8px',
});

const TotalCount = styled('span')({
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
});


const EmptyMessage = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 0',
  fontSize: '14px',
  color: '#999',
});

// ============================================

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'APPROVED', label: '승인' },
  { value: 'APPROVAL_PENDING', label: '대기' },
  { value: 'REFUSED', label: '불가' },
  { value: 'WITHDREW', label: '탈퇴' },
];

// 기능 사용 여부 필터(#189). 값이 '' 이면 거르지 않는다.
const USE_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'Y', label: '사용' },
  { value: 'N', label: '사용 안 함' },
];

const FEATURE_FILTERS = [
  { key: 'facilityControlYn', label: 'IoT', options: USE_OPTIONS },
  { key: 'doorSystemYn', label: '안면인식 출입문', options: USE_OPTIONS },
  { key: 'autoDeductionYn', label: '자동 차감', options: USE_OPTIONS },
  { key: 'appBrandingType', label: '회원앱 브랜딩', options: [{ value: '', label: '전체' }, ...appBrandingTypes] },
];

const initialQuery = {
  page: 1,
  limit: 10,
  status: '',
  name: '',
  registeredDateFrom: '',
  registeredDateTo: '',
  facilityControlYn: '',
  doorSystemYn: '',
  autoDeductionYn: '',
  appBrandingType: '',
};

var qs = require('qs');

const CorpManagement = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const token = useSelector(state => state.user.user.token);
  const [loading, setLoading] = useState(false);
  const [managers, setManagers] = useState();
  const [filterOpen, setFilterOpen] = useState(true);
  const [excelUploadOpen, setExcelUploadOpen] = useState(false);
  const [query, setQuery] = useState(() => {
    const saved = sessionStorage.getItem('corpSearchConditions');
    if (saved) {
      sessionStorage.removeItem('corpSearchConditions');
      try { return JSON.parse(saved); } catch { return initialQuery; }
    }
    return initialQuery;
  });

  const { handleSubmit, control, reset, setValue, watch } = useForm({ defaultValues: initialQuery });

  // 마지막으로 조회한 조건. 페이지·개수 변경은 이 조건으로 다시 부른다 —
  // 필터 라디오만 바꾸고 [검색]을 안 누른 상태에서 페이지를 넘기면 미확정 필터가 섞여 '총 N건인데 0행'이 됐다(#189 QA)
  const appliedQueryRef = useRef(query);

  const handleChangePage = (_, value) => {
    const next = { ...appliedQueryRef.current, page: value };
    setQuery(next);
    getManagerList(next);
  };

  const handleChangeLimit = e => {
    const next = { ...appliedQueryRef.current, limit: e.target.value, page: 1 };
    setQuery(next);
    getManagerList(next);
  };

  const handleChange = e => {
    const { name, checked } = e.target;
    let tempUser;
    if (name === 'allSelect') {
      tempUser = managers?.content?.map(user => ({ ...user, isChecked: checked }));
    } else {
      tempUser = managers?.content?.map(manager =>
        manager.username + manager.id === name ? { ...manager, isChecked: checked } : manager,
      );
    }
    setManagers({ ...managers, content: tempUser });
  };

  useEffect(() => {
    getManagerList(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getManagerList = async queries => {
    setQuery(prev => ({ ...prev, ...queries }));
    appliedQueryRef.current = { ...query, ...queries };
    const queryString = qs.stringify(appliedQueryRef.current);
    try {
      setLoading(true);
      const result = await getAllManagerList(token, queryString);
      setManagers(result);
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setLoading(false);
    }
  };

  const submit = values => {
    const from = values.registeredDateFrom ? new Date(values.registeredDateFrom).toISOString().split('T')[0] : '';
    const to = values.registeredDateTo ? new Date(values.registeredDateTo).toISOString().split('T')[0] : '';

    getManagerList({
      ...query,
      page: 1,
      name: values.name,
      status: query.status,
      registeredDateFrom: from,
      registeredDateTo: to,
    });
  };

  const setInitial = () => {
    setQuery(initialQuery);
    reset(initialQuery);
    getManagerList(initialQuery);
  };

  const exportToExcel = () => {
    const checkedFilteredData = managers?.content?.filter(data => data.isChecked);
    const filteredData = checkedFilteredData.map(
      ({ businessName, name, phoneNumber, registeredDate, status, username }) => ({
        '사업자명': businessName,
        '이름': name,
        '연락처': phoneNumber,
        '가입일': registeredDate,
        '상태': status,
        '아이디': username,
      }),
    );

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(filteredData);
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'corp_members.xlsx');
  };

  return (
    <AnimationPage>
      <PageContainer>
        {/* Header */}
        <HeaderRow>
          <SearchSection>
            <PageTitle>{t('기업회원 관리')}</PageTitle>
            <FormInputText
              control={control}
              name="name"
              sx={{ width: '370px' }}
              placeholder="사업자명, 닉네임, 휴대폰번호를 검색해 주세요"
              onKeyDown={event => {
                if (event.key === 'Enter') handleSubmit(submit)();
              }}
            />
            <CommonSearchSubmitButton onClick={handleSubmit(submit)}>
              {t('Search')}
            </CommonSearchSubmitButton>
          </SearchSection>

          <HeaderButtons>
            <ExcelUploadButton type="button" onClick={() => setExcelUploadOpen(true)}>
              엑셀로 업로드하기
            </ExcelUploadButton>
            <Link to="/corp/create" style={{ textDecoration: 'none' }}>
              <CreateButton as="span">기업회원 등록</CreateButton>
            </Link>
          </HeaderButtons>
        </HeaderRow>

        <CorpExcelUploadModal
          open={excelUploadOpen}
          onClose={() => setExcelUploadOpen(false)}
          onUploaded={() => getManagerList(query)}
        />

        {/* Filters */}
        <Collapse in={filterOpen}>
          <FilterRow>
            <FilterLabel>회원구분</FilterLabel>
            <RadioGroup
              row
              value={query.status}
              onChange={e => setQuery({ ...query, status: e.target.value })}>
              {STATUS_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" sx={{ color: '#aaa', '&.Mui-checked': { color: '#0F7F85' } }} />}
                  label={option.label}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                />
              ))}
            </RadioGroup>
          </FilterRow>

          {FEATURE_FILTERS.map(filter => (
            <FilterRow key={filter.key}>
              <FilterLabel>{filter.label}</FilterLabel>
              <RadioGroup
                row
                value={query[filter.key]}
                onChange={e => setQuery({ ...query, [filter.key]: e.target.value })}>
                {filter.options.map(option => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size="small" sx={{ color: '#aaa', '&.Mui-checked': { color: '#0F7F85' } }} />}
                    label={option.label}
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                  />
                ))}
              </RadioGroup>
            </FilterRow>
          ))}

          <FilterRow>
            <FilterLabel>{t('Period')}</FilterLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FormInputDate name="registeredDateFrom" control={control} placeholder="시작일" />
              <Typography sx={{ color: '#999' }}>~</Typography>
              <FormInputDate name="registeredDateTo" control={control} placeholder="종료일" minDate={watch('registeredDateFrom') || null} />
              <FilterButtons setValue={setValue} watch={watch} />
            </Box>
          </FilterRow>

          {/* Search Buttons */}
          <SearchButtonRow>
            <CommonFilterActionButton variant="primary" onClick={handleSubmit(submit)}>
              {t('Search for members with set conditions')}
            </CommonFilterActionButton>
            <CommonFilterActionButton variant="secondary" onClick={setInitial}>
              {t('Reset search conditions')}
            </CommonFilterActionButton>
          </SearchButtonRow>
        </Collapse>

        <CommonFilterToggle open={filterOpen} onClick={() => setFilterOpen(prev => !prev)} />

        {/* Total + Limit Select */}
        <LimitSelectRow>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <TotalCount>총 {managers?.totalElements || 0}건</TotalCount>
            <CommonExcelButton
              onClick={exportToExcel}
              disabled={loading || !managers?.content?.some(user => user.isChecked)}>
              엑셀 다운로드
            </CommonExcelButton>
          </Box>
          <CommonSelect
            value={query.limit}
            onChange={handleChangeLimit}
            width={108}
            options={pageSize}
          />
        </LimitSelectRow>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Loader />
          </Box>
        ) : managers?.content?.length > 0 ? (
          <>
            <CorpManagementTable
              managers={managers}
              setManagers={setManagers}
              handleChange={handleChange}
              // 조회한 조건만 남긴다(검색을 안 누른 라디오 값은 빼고)
              onRowClick={() => sessionStorage.setItem('corpSearchConditions', JSON.stringify(appliedQueryRef.current))}
            />
            <CommonPagination
              totalElements={managers?.totalElements}
              limit={query.limit}
              page={query.page}
              onChange={handleChangePage}
            />
          </>
        ) : (
          <EmptyMessage>검색 결과가 없습니다.</EmptyMessage>
        )}
      </PageContainer>
    </AnimationPage>
  );
};

export default CorpManagement;
